import type { RouteRecordRaw } from 'vue-router'

/**
 * Backwards compatibility redirects for the behandelaar app.
 *
 * These redirect old Dutch paths to new English paths after the URL
 * internationalization effort. Can be removed after migration period (Q3 2025).
 *
 * Categories:
 * - /instellingen/* → /organization/* (Dutch settings paths)
 * - /settings/* → /organization/* (intermediate English paths)
 * - /organization/[dutch]/* → /organization/[english]/* (partially translated paths)
 * - /clients/:id/[dutch]/* → /clients/:id/[english]/* (client subpaths)
 * - /declaraties/* → /claims/* (billing paths)
 * - /clients-preview, /clients/:id/dossier-preview/* (the retired redesign, #4501)
 * - Other legacy paths
 */

/**
 * The retired preview's `:view` slug → the classic page below `/clients/:clientId`
 * showing the same thing. An unknown slug falls back to Overzicht (below), so a
 * mistyped or half-remembered URL still lands in the dossier.
 */
const PREVIEW_VIEW_TO_CLASSIC_PATH: Record<string, string> = {
  overzicht: '/dossier/overview',
  profiel: '',
  afspraken: '/dossier/appointments',
  documenten: '/dossier/documents',
  formulieren: '/dossier/answers',
  zorgkring: '/dossier/zorgkring',
  zorgtraject: '/care-trajectories'
}

export const behandelaarRedirects: RouteRecordRaw[] = [
  // ============================================================================
  // /instellingen/* → /organization/* redirects
  // ============================================================================
  {
    // Roles are created and edited in modals on the overview (#3212), so both
    // legacy editor URLs land on the overview rather than a per-role page.
    path: '/instellingen/rollen/nieuw',
    redirect: '/organization/roles'
  },
  {
    path: '/instellingen/rollen/:roleId',
    redirect: '/organization/roles'
  },
  {
    path: '/instellingen/brieftemplates',
    redirect: '/organization/letter-templates'
  },
  {
    path: '/instellingen/brieftemplates/nieuw',
    redirect: '/organization/letter-templates/new'
  },
  {
    path: '/instellingen/brieftemplates/:id/bewerken',
    redirect: to => `/organization/letter-templates/${to.params.id}`
  },

  // ============================================================================
  // /settings/* → /organization/* redirects (intermediate English paths)
  // ============================================================================
  {
    path: '/settings/roles/new',
    redirect: '/organization/roles'
  },
  {
    path: '/settings/roles/:roleId',
    redirect: '/organization/roles'
  },
  {
    path: '/settings/letter-templates',
    redirect: '/organization/letter-templates'
  },
  {
    path: '/settings/letter-templates/new',
    redirect: '/organization/letter-templates/new'
  },
  {
    path: '/settings/letter-templates/:id/edit',
    redirect: to => `/organization/letter-templates/${to.params.id}`
  },
  {
    path: '/settings/signaleringen',
    redirect: '/organization/signals'
  },
  {
    path: '/settings/signal-definitions',
    redirect: '/organization/signals'
  },

  // ============================================================================
  // /organization/[dutch]/* → /organization/[english]/* redirects
  // ============================================================================
  {
    path: '/organization/afspraaktypes',
    redirect: '/organization/appointment-types'
  },
  {
    path: '/organization/afspraaktypes/:appointmentTypeId',
    redirect: to => `/organization/appointment-types/${to.params.appointmentTypeId}`
  },
  {
    path: '/organization/clientstatussen',
    redirect: '/organization/client-states'
  },
  {
    path: '/organization/clientstatussen/:clientStateId',
    redirect: to => `/organization/client-states/${to.params.clientStateId}`
  },
  {
    // Certificate management moved into the tabbed Integrations view (#2488).
    path: '/organization/certificaten',
    redirect: '/organization/integrations?tab=certificates'
  },
  {
    // Organisatiegegevens was split into the hub's own cards; practice details is its heart.
    path: '/organization/manage',
    redirect: '/organization/practice'
  },

  // ============================================================================
  // /formulieren/* → /form-submissions/* redirects
  // ============================================================================
  {
    path: '/formulieren',
    redirect: '/form-submissions'
  },
  {
    path: '/formulieren/:id',
    redirect: to => `/form-submissions/${to.params.id}`
  },

  // ============================================================================
  // /clients/:id/[dutch]/* → /clients/:id/[english]/* redirects
  // ============================================================================
  {
    path: '/clients/:clientId/zorgtrajecten',
    redirect: to => `/clients/${to.params.clientId}/care-trajectories`
  },
  {
    path: '/clients/:clientId/zorgtrajecten/:zorgtrajectId',
    redirect: to => `/clients/${to.params.clientId}/care-trajectories/${to.params.zorgtrajectId}`
  },
  {
    path: '/clients/:clientId/financiering',
    redirect: to => `/clients/${to.params.clientId}/billing`
  },
  {
    path: '/clients/:clientId/dossier/overzicht',
    redirect: to => `/clients/${to.params.clientId}/dossier/overview`
  },
  {
    path: '/clients/:clientId/dossier/documenten',
    redirect: to => `/clients/${to.params.clientId}/dossier/documents`
  },
  {
    path: '/clients/:clientId/dossier/verslagen',
    redirect: to => `/clients/${to.params.clientId}/dossier/overview`
  },
  {
    path: '/clients/:clientId/dossier/verslagen/:reportId',
    redirect: to => `/clients/${to.params.clientId}/dossier/overview`
  },
  {
    path: '/clients/:clientId/dossier/afspraken',
    redirect: to => `/clients/${to.params.clientId}/dossier/appointments`
  },
  {
    path: '/clients/:clientId/dossier/afspraken/:appointmentId',
    redirect: to => `/clients/${to.params.clientId}/dossier/appointments/${to.params.appointmentId}`
  },
  {
    path: '/clients/:clientId/dossier/antwoorden',
    redirect: to => `/clients/${to.params.clientId}/dossier/answers`
  },

  // ============================================================================
  // /clients-preview, /clients/:id/dossier-preview/* (redesigned dossier retired)
  //
  // The preview tree of epic #3144 is gone (#4501). The router has no catch-all,
  // so a bookmarked or shared preview URL would otherwise resolve to no record
  // at all and render a blank shell rather than a page. Each preview sub-view
  // lands on the classic page showing the same thing — the map that
  // `dossier-preview-redirect.ts` used to hold.
  // ============================================================================
  {
    path: '/clients-preview',
    redirect: '/clients'
  },
  {
    path: '/clients/:clientId/dossier-preview',
    // The shell's own route carried no `:view` and opened on Overzicht.
    redirect: to => `/clients/${to.params.clientId}/dossier/overview`
  },
  {
    path: '/clients/:clientId/dossier-preview/:view',
    redirect: to => {
      const classic = PREVIEW_VIEW_TO_CLASSIC_PATH[to.params.view as string]
      return `/clients/${to.params.clientId}${classic ?? '/dossier/overview'}`
    }
  },

  // ============================================================================
  // /clients/:id/dossier/reports/* redirects (reports functionality removed)
  // ============================================================================
  {
    path: '/clients/:clientId/dossier/reports',
    redirect: to => `/clients/${to.params.clientId}/dossier/overview`
  },
  {
    path: '/clients/:clientId/dossier/reports/:reportId',
    redirect: to => `/clients/${to.params.clientId}/dossier/overview`
  },

  // ============================================================================
  // /mijn-agenda/* → /agenda/* and /appointments/* redirects
  // ============================================================================
  {
    path: '/mijn-agenda',
    redirect: '/agenda?view=list'
  },
  {
    // Skip the intermediate /agenda/:appointmentId hop (which itself redirects
    // to appointment-detail) and go straight to the standalone detail page.
    path: '/mijn-agenda/:appointmentId',
    redirect: to => ({ name: 'appointment-detail', params: { id: to.params.appointmentId } })
  },

  // ============================================================================
  // /facturen → /invoices redirect
  // ============================================================================
  {
    path: '/facturen',
    redirect: '/invoices'
  },

  // ============================================================================
  // /declaraties/* → /claims/* redirects
  // ============================================================================
  {
    path: '/declaraties',
    redirect: '/claims'
  },
  {
    path: '/declaraties/zorgverzekeraar',
    redirect: '/claims/insurer'
  },
  {
    path: '/declaraties/batches',
    redirect: '/claims/batches'
  },
  {
    path: '/declaraties/particulier',
    redirect: '/claims/private'
  },
  {
    path: '/declaraties/facturen',
    redirect: '/claims/invoices'
  },
  {
    path: '/declaraties/vecozo',
    redirect: '/claims/insurer'
  },
  {
    path: '/declaraties/brief',
    redirect: '/claims/private'
  },
  {
    path: '/declaraties/brief/openstaand',
    redirect: '/claims/private'
  },
  {
    path: '/declaraties/brief/facturen',
    redirect: '/claims/invoices'
  },
  {
    path: '/declaraties/brief/verstuurd',
    redirect: '/claims/invoices'
  },
  {
    path: '/declaraties/brief/verwerkt',
    redirect: '/claims/invoices'
  },
  {
    path: '/declaraties/uitstaand',
    redirect: '/claims/insurer'
  },
  {
    path: '/declaraties/verwerkt',
    redirect: '/claims/invoices'
  }
]
